<template>
    <div class="max-w-7xl mx-auto pt-0 pb-4 px-2 lg:px-3">
        <!-- Header -->
        <div class="mb-4 text-center">
            <h1 class="text-3xl sm:text-[2.15rem] font-extrabold text-gray-900 mb-1">Assinar Sirimmatrix</h1>
            <p class="text-gray-500 text-sm">Acesso completo à plataforma. Cancele quando quiser.</p>
        </div>

        <div v-if="!loadingSubscription && paymentPending" class="mb-4 mx-auto max-w-3xl p-4 bg-red-50 border border-red-200 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3">
            <div class="flex-1">
                <p class="text-sm text-red-700 font-semibold">Pagamento pendente</p>
                <p class="text-xs text-red-600 mt-0.5">A última cobrança da sua assinatura foi recusada. Atualize o cartão em Minha Conta para regularizar — a troca de plano fica liberada depois.</p>
            </div>
            <router-link :to="{ name: 'account' }" class="btn btn-sm btn-error text-white shrink-0">Atualizar cartão</router-link>
        </div>

        <div v-else-if="!loadingSubscription && endedSubscription" class="mb-4 mx-auto max-w-3xl p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p class="text-sm text-amber-800 font-semibold">
                Sua assinatura foi encerrada em {{ formatDate(endedSubscription.ended_at) }}{{ endedSubscription.reason === 'payment_failed' ? ' porque o pagamento foi recusado' : '' }}.
            </p>
            <p class="text-xs text-amber-700 mt-0.5">Escolha um plano abaixo e informe um novo cartão na tela de pagamento.</p>
        </div>

        <div v-if="loadingSubscription" class="grid gap-4 lg:grid-cols-3">
            <div v-for="i in 3" :key="i" class="rounded-[24px] p-4 bg-white border border-gray-200 shadow-sm">
                <div class="skeleton mx-auto h-6 w-32 rounded-full"></div>
                <div class="mt-3 flex items-center justify-between gap-2 border-b border-gray-100 pb-2.5">
                    <div class="skeleton h-6 w-32 rounded"></div>
                    <div class="skeleton h-6 w-24 rounded"></div>
                </div>
                <div class="mt-3 space-y-1">
                    <div v-for="j in 9" :key="j" class="skeleton h-4 w-full rounded"></div>
                </div>
                <div class="skeleton h-12 w-full rounded-xl mt-4"></div>
            </div>
        </div>

        <div v-else class="grid gap-4 lg:grid-cols-3">
            <div
                v-for="plan in plans"
                :key="plan.id"
                :class="[
                    'flex h-full flex-col rounded-[24px] p-4 lg:p-5 transition-all',
                    plan.id === 'premium'
                        ? 'bg-gradient-to-b from-amber-50 via-white to-white border-2 border-amber-500 shadow-lg shadow-amber-100/70'
                        : plan.highlight
                        ? 'bg-gradient-to-b from-cyan-50 via-white to-white border-2 border-cyan-500 shadow-lg shadow-cyan-100/70'
                        : 'bg-white border border-gray-200 shadow-sm'
                ]"
            >
                <div class="mb-3 flex min-h-[24px] items-start justify-center">
                    <span
                        v-if="plan.highlight"
                        :class="[
                            'inline-flex items-center rounded-full px-4 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-white shadow-lg',
                            plan.id === 'premium' ? 'bg-amber-600' : 'bg-cyan-600'
                        ]"
                    >
                        {{ plan.badge }}
                    </span>
                </div>

                <div class="flex items-baseline justify-between gap-2 border-b border-gray-100 pb-2.5">
                    <h2 class="text-[1.3rem] lg:text-[1.45rem] font-black tracking-[-0.02em] leading-none text-gray-800">{{ plan.name }}</h2>
                    <div class="shrink-0 whitespace-nowrap text-right">
                        <span
                            :class="[
                                'text-[1.4rem] lg:text-[1.6rem] font-black tracking-[-0.03em] leading-none',
                                plan.id === 'premium' ? 'text-amber-700' : plan.highlight ? 'text-cyan-700' : 'text-gray-900'
                            ]"
                        >
                            {{ plan.priceLabel }}
                        </span>
                        <span class="ml-0.5 text-xs text-gray-400">/{{ plan.interval }}</span>
                    </div>
                </div>

                <ul class="mt-3 flex-1 space-y-1 text-xs leading-4 text-gray-700">
                    <li v-for="feature in planFeatures(plan)" :key="feature.key" class="flex items-start gap-2">
                        <span
                            :class="[
                                'mt-px inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                                !feature.included
                                    ? 'bg-red-100 text-red-500'
                                    : feature.highlight
                                    ? plan.id === 'premium'
                                        ? 'bg-amber-100 text-amber-600'
                                        : 'bg-cyan-100 text-cyan-600'
                                    : 'bg-emerald-100 text-emerald-600'
                            ]"
                        >
                            <svg v-if="feature.included" xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"/></svg>
                        </span>
                        <span
                            :class="[
                                !feature.included
                                    ? 'text-gray-400'
                                    : feature.highlight
                                    ? plan.id === 'premium'
                                        ? 'font-semibold text-amber-700'
                                        : 'font-semibold text-cyan-700'
                                    : ''
                            ]"
                        >{{ feature.label }}</span>
                    </li>
                </ul>

                <div class="mt-3 pt-1">
                    <button
                        v-if="alreadySubscribed && plan.id === currentPlanId"
                        class="btn btn-block h-12 rounded-xl text-base font-semibold border-0 bg-gray-100 text-gray-400"
                        disabled
                    >Seu plano atual</button>
                    <button
                        v-else-if="alreadySubscribed"
                        :class="[
                            'btn btn-block h-12 rounded-xl text-base font-semibold text-white shadow border-0',
                            plan.id === 'premium' ? 'bg-amber-600 hover:bg-amber-700' : plan.highlight ? 'bg-cyan-600 hover:bg-cyan-700' : 'btn-primary'
                        ]"
                        :disabled="changingPlan || paymentPending"
                        @click="openChangePlanModal(plan)"
                    >
                        <span v-if="changingPlan" class="loading loading-spinner loading-sm"></span>
                        <span v-else>{{ isUpgrade(plan) ? 'Fazer upgrade' : 'Fazer downgrade' }}</span>
                    </button>
                    <button
                        v-else
                        :class="[
                            'btn btn-block h-12 rounded-xl text-base font-semibold text-white shadow border-0',
                            plan.id === 'premium' ? 'bg-amber-600 hover:bg-amber-700' : plan.highlight ? 'bg-cyan-600 hover:bg-cyan-700' : 'btn-primary'
                        ]"
                        :disabled="loading"
                        @click="subscribe(plan)"
                    >
                        <span v-if="loading" class="loading loading-spinner loading-sm"></span>
                        <span v-else>{{ plan.ctaLabel || 'Assinar agora' }}</span>
                    </button>
                </div>
            </div>
        </div>

        <p v-if="errorMessage" class="text-center text-sm text-red-600 mt-2">{{ errorMessage }}</p>

        <p class="text-center text-xs text-gray-400 mt-3">
            Pagamento seguro via Stripe. Cancele a qualquer momento em Minha Conta.
        </p>

        <!-- Change plan confirm modal -->
        <dialog ref="changePlanModalRef" class="modal">
            <div class="modal-box w-11/12 max-w-lg" v-if="planToChange">
                <h3 class="font-bold text-lg text-gray-800">
                    {{ isUpgrade(planToChange) ? 'Fazer upgrade' : 'Fazer downgrade' }} para {{ planToChange.name }}?
                </h3>
                <p class="py-3 text-sm text-gray-600">
                    Sua assinatura passa a cobrar <strong>{{ planToChange.priceLabel }}/{{ planToChange.interval }}</strong>.
                    O ajuste é proporcional ao período já usado do ciclo atual — a próxima cobrança na fatura considera essa diferença.
                </p>
                <p v-if="changePlanError" class="text-red-600 text-xs">{{ changePlanError }}</p>
                <div class="modal-action flex-col sm:flex-row">
                    <button class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight" @click="closeChangePlanModal" :disabled="changingPlan">Cancelar</button>
                    <button
                        class="btn btn-xs !text-xs !px-3 !py-1.5 !min-h-0 !h-auto !leading-tight !text-white !bg-cyan-600 hover:!bg-cyan-700"
                        :disabled="changingPlan"
                        @click="confirmChangePlan"
                    >
                        {{ changingPlan ? 'Trocando...' : 'Confirmar troca' }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop"><button>close</button></form>
        </dialog>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { SubscriptionService } from '@/services/Entity';
import AuthService from '@/services/AuthService';
import { PLANS, getPlanFeatures } from '@/constants/plans';

export default {
    setup() {
        const loading = ref(false);
        const loadingSubscription = ref(true);
        const errorMessage = ref('');
        const alreadySubscribed = ref(false);
        const paymentPending = ref(false);
        const endedSubscription = ref(null);
        const currentPlanId = ref(null);
        const plans = PLANS;

        onMounted(async () => {
            try {
                const { data } = await SubscriptionService.getActiveSubscription();
                if (data && data.active) {
                    alreadySubscribed.value = true;
                    currentPlanId.value = data.plan || 'basico';
                    // past_due/unpaid: assinatura existe, mas o caminho é trocar o cartão, não o plano.
                    paymentPending.value = data.status !== 'active' && data.status !== 'trialing';
                } else if (data && data.ended) {
                    endedSubscription.value = data.ended;
                }
            } catch (e) { /* silent */ } finally {
                loadingSubscription.value = false;
            }
        });

        function isUpgrade(plan) {
            const current = plans.find(p => p.id === currentPlanId.value);
            if (!current) return true;
            return (plan.rank || 0) > (current.rank || 0);
        }

        const changePlanModalRef = ref(null);
        const planToChange = ref(null);
        const changingPlan = ref(false);
        const changePlanError = ref('');

        function openChangePlanModal(plan) {
            planToChange.value = plan;
            changePlanError.value = '';
            changePlanModalRef.value?.showModal();
        }

        function closeChangePlanModal() {
            changePlanModalRef.value?.close();
            planToChange.value = null;
        }

        async function confirmChangePlan() {
            if (!planToChange.value) return;
            changingPlan.value = true;
            changePlanError.value = '';
            try {
                await SubscriptionService.changePlan(planToChange.value.id);
                await AuthService.refreshSession();
                currentPlanId.value = planToChange.value.id;
                closeChangePlanModal();
            } catch (err) {
                changePlanError.value = err?.response?.data?.message || 'Erro ao trocar de plano. Tente novamente.';
            } finally {
                changingPlan.value = false;
            }
        }

        async function subscribe(plan) {
            loading.value = true;
            errorMessage.value = '';
            try {
                const { data } = await SubscriptionService.createCheckoutSession(plan.id);
                if (data && data.url) {
                    window.location.href = data.url;
                } else if (data && data.status === 'already_subscribed') {
                    alreadySubscribed.value = true;
                } else {
                    errorMessage.value = 'Erro ao iniciar checkout. Tente novamente.';
                }
            } catch (err) {
                errorMessage.value = 'Erro ao iniciar checkout. Tente novamente.';
                console.error(err);
            } finally {
                loading.value = false;
            }
        }

        function planFeatures(plan) {
            return getPlanFeatures(plan);
        }

        function formatDate(unixTs) {
            if (!unixTs) return '—';
            return new Date(unixTs * 1000).toLocaleDateString('pt-BR');
        }

        return {
            loading, loadingSubscription, errorMessage, alreadySubscribed, currentPlanId, subscribe, plans, planFeatures, isUpgrade,
            paymentPending, endedSubscription, formatDate,
            changePlanModalRef, planToChange, changingPlan, changePlanError,
            openChangePlanModal, closeChangePlanModal, confirmChangePlan
        };
    }
};
</script>
